import React, { FC } from "react";
import type { MessageModalProps } from "@/types/components/p2p-money-abroad/message-modal";
import { FormText, FormCheck } from "../form";

type FormInput = {
  value: string | boolean;
  errorMessage?: string;
  fieldName: string;
  selected?: boolean;
};

type FormState = {
  [key: string]: FormInput;
};

export const QuestionForm: FC<{
  onSubmit: (event: React.FormEvent) => void;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  isModal?: boolean;
  className?: string;
}> = ({ onSubmit, form, setForm, isModal, className }) => {
  return (
    <form className={`messageContactForm ${className || ""}`} onSubmit={onSubmit}>
      <div className="messageContactForm--fields">
        <div className="messageContactForm--input input-left">
          <FormText
            label="Full Name"
            value={form.name.value as string}
            required
            type="text"
            inputAttrs={{
              id: "input_5",
              name: "q5_fullName",
            }}
            errorMessage={form.name.errorMessage}
            onChange={(value) => {
              setForm((form) => ({
                ...form,
                name: {
                  value,
                  errorMessage: "",
                  fieldName: "q5_fullName",
                },
              }));
            }}
          />
        </div>
        <div className="messageContactForm--input">
          <FormText
            label="Company Email"
            value={form.email.value as string}
            required
            type="email"
            inputAttrs={{
              id: "input_4",
              name: "q4_email",
            }}
            errorMessage={form.email.errorMessage}
            onChange={(value) => {
              setForm((form) => ({
                ...form,
                email: {
                  value,
                  errorMessage: "",
                  fieldName: "q4_email",
                },
              }));
            }}
          />
        </div>
        <div className="messageContactForm--input input-left">
          <FormText
            label="Phone Number"
            value={form.phone.value as string}
            required
            type="tel"
            inputAttrs={{
              id: "input_6_full",
              name: "q6_phoneNumber[full]",
            }}
            onChange={(value) => {
              setForm((form) => ({
                ...form,
                phone: {
                  value,
                  errorMessage: "",
                  fieldName: "q6_phoneNumber[full]",
                },
              }));
            }}
          />
        </div>
        <div className="messageContactForm--input">
          <FormText
            label="Company Name"
            value={form.companyName.value as string}
            required
            type="text"
            inputAttrs={{
              id: "input_7",
              name: "q7_companyName",
            }}
            errorMessage={form.companyName.errorMessage}
            onChange={(value) => {
              setForm((form) => ({
                ...form,
                companyName: {
                  value,
                  errorMessage: "",
                  fieldName: "q7_companyName",
                },
              }));
            }}
          />
        </div>
        <div className="messageContactForm--question">
          <FormText
            label="Send a question..."
            value={form.sendAQuestionText.value as string}
            type="textarea"
            inputAttrs={{
              id: "input_8",
              name: "q8_sendA",
            }}
            errorMessage={form.sendAQuestionText.errorMessage}
            onChange={(value) => {
              setForm((form) => ({
                ...form,
                sendAQuestionText: {
                  value,
                  errorMessage: "",
                  fieldName: "q8_sendA",
                },
              }));
            }}
          />
        </div>
        <div className="messageContactForm--checks">
          <div className="messageContactForm--box">
            <FormCheck
              label="Register for a 30-minute demo"
              value={form.registerForA30MinuteDemo.selected as boolean}
              errorMessage={form.registerForA30MinuteDemo.errorMessage}
              onChange={(value) => {
                setForm((form) => ({
                  ...form,
                  registerForA30MinuteDemo: {
                    ...form.registerForA30MinuteDemo,
                    selected: value,
                    errorMessage: "",
                    fieldName: "registerForA30MinuteDemo",
                  },
                }));
              }}
            />
          </div>
          <div className="messageContactForm--box">
            <FormCheck
              label="Receive preliminary assessment form"
              value={form.receivePreliminaryAssessmentForm.selected as boolean}
              inputAttrs={{
                id: "input_9_1",
                name: "q9_iAm[]",
              }}
              errorMessage={form.receivePreliminaryAssessmentForm.errorMessage}
              onChange={(value) => {
                setForm((form) => ({
                  ...form,
                  receivePreliminaryAssessmentForm: {
                    ...form.receivePreliminaryAssessmentForm,
                    selected: value,
                    errorMessage: "",
                    fieldName: "q9_iAm[]",
                  },
                }));
              }}
            />
          </div>
        </div>
        <div className="messageContactForm--bottom">
          <button
            type="submit"
            className="link link_full link_blue messageContactForm--submit"
            data-title="Submit"
          >
            <span>Submit</span>
          </button>
        </div>
      </div>
    </form>
  );
};

export const MessageForm: FC<MessageModalProps> = ({ title }) => {
  const [formSuccess, setFormSuccess] = React.useState<boolean | null>(null);

  const [form, setForm] = React.useState<{
    [key: string]: FormInput;
  }>({
    name: {
      value: "",
      errorMessage: "",
      fieldName: "q5_fullName",
    },
    email: {
      value: "",
      errorMessage: "",
      fieldName: "q4_email",
    },
    phone: {
      value: "",
      fieldName: "q6_phoneNumber[full]",
    },
    companyName: {
      value: "",
      errorMessage: "",
      fieldName: "q7_companyName",
    },
    sendAQuestionText: {
      value: "",
      errorMessage: "",
      fieldName: "q8_sendA",
    },
    registerForA30MinuteDemo: {
      selected: false,
      value: 'Register for a 30 minute demo',
      errorMessage: "",
      fieldName: "q9_iAm[]",
    },
    receivePreliminaryAssessmentForm: {
      selected: false,
      value: 'Receive preliminary assessment form',
      errorMessage: "",
      fieldName: "q9_iAm[]",
    },
    receiveOtherCommunications: {
      value: false,
      errorMessage: "",
      fieldName: "q14_iAgree",
    },
  });

  const resetFormValues = () => {
    Object.keys(form).forEach((key) => {
      setForm((form) => ({
        ...form,
        [key]: {
          ...form[key],
          value: "",
        },
      }));
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const requiredFields = [
      "name",
      "email",
      "phone",
      "companyName",
    ];

    const checkboxFields = [
      "registerForA30MinuteDemo",
      "receivePreliminaryAssessmentForm",
    ]

    const invalidFields = requiredFields.filter((field) => {
      if (!form[field].value) {
        setForm((form) => ({
          ...form,
          [field]: {
            ...form[field],
            errorMessage: "This field is required",
          },
        }));
        return true;
      }
      return false;
    });

    const checkboxFieldsCheck = checkboxFields.filter((field) => {
      if (!form[field].selected) {
        setForm((form) => ({
          ...form,
          [field]: {
            ...form[field],
            errorMessage: "At least one checkbox is required",
          },
        }));
        return true;
      }
      return false;
    });

    if (invalidFields.length > 0 || checkboxFieldsCheck.length < 1) {
      return;
    } else {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        formData.append(form[key].fieldName, JSON.stringify(form[key].value).replace(/"/g, ''));
      });
      try {
        const response = await fetch(
          "https://eu-submit.jotform.com/submit/241753585100048",
          {
            method: "POST",
            body: formData,
          }
        );

        if (response.ok) {
          console.log("Form submitted successfully");
          setFormSuccess(true);
          resetFormValues();
        } else {
          setFormSuccess(false);
          console.error("Form submission failed");
        }
      } catch (error) {
        setFormSuccess(false);
        console.error("An error occurred during form submission:", error);
      } finally {
        window.scrollTo({
          top: scrollY,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <div className="wrapper">
      <section className="messageForm">
        {!formSuccess && <div className="messageForm--top">
          <div className="messageForm--title">{title}</div>
        </div>}
        <div className="messageForm--inner">
          <div className="messageForm--main">
            {formSuccess === null && (
              <QuestionForm
                onSubmit={handleSubmit}
                form={form}
                setForm={setForm}
                isModal
                className="messageForm--form"
              />
            )}
            {formSuccess === true && (
              <div className="messageForm--success">
                Thank You!
                <br />
                <br />
                Your submission has been received.
              </div>
            )}
            {formSuccess === false && (
              <div className="messageForm--error">
                There was an error, please try again later.
              </div>
            )}
          </div>
          <div className="messageForm--footer">
            {formSuccess === null && (
              <button onClick={handleSubmit} className="link link_full link_blue">
                Submit
              </button>
            )}
          </div>
          {!formSuccess &&
            <div className="messageForm--letsTalk">
              <div className="messageForm--title">Let&apos; s talk!</div>
            </div>
          }
        </div>
      </section>
    </div>
  );
};
